import React, { useEffect, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, KeyRound, Mail, MessageSquare, Webhook, ExternalLink, Copy, CheckCircle2, RefreshCw, AlertTriangle, Lock } from "lucide-react";
import SmsAutomationsTab from "@/components/app/SmsAutomationsTab";

export default function AdminSettings() {
  const [values, setValues] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const [closeRes, setCloseRes] = useState(null);
  const [closeBusy, setCloseBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setValues(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load settings");
    }
  };
  useEffect(() => { load(); }, []);

  const patch = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const save = async (fields) => {
    setBusy(true);
    try {
      const payload = {};
      for (const f of fields) {
        const v = values?.[f];
        if (v == null) continue;
        // Skip the masked placeholder so we don't overwrite the stored secret with dots
        if (typeof v === "string" && /^[•*.\s]+$/.test(v)) continue;
        payload[f] = v;
      }
      const { data } = await api.put("/admin/settings", payload);
      setValues(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const copyUrl = (url, key) => {
    navigator.clipboard.writeText(url);
    setCopied(key);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(""), 1500);
  };

  const fetchCloseResources = async () => {
    setCloseBusy(true);
    try {
      const { data } = await api.get("/admin/close/resources");
      setCloseRes(data);
      toast.success(`Connected as ${data.me?.name || "Close user"} · found ${data.sequences?.length || 0} sequences`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Fetch failed");
    } finally { setCloseBusy(false); }
  };

  const runBackfill = async () => {
    if (!window.confirm("Create ClientQuack accounts for every lead currently in Close CRM?\n\nEach lead gets a portal account with the deterministic password (LastName + last 4 of phone). Existing accounts are skipped.")) return;
    setCloseBusy(true);
    try {
      const { data } = await api.post("/admin/close/backfill-accounts");
      toast.success(`Backfill complete · ${data.total_created} created · ${data.total_skipped} skipped · ${data.total_errors} errors`, { duration: 12000 });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Backfill failed");
    } finally { setCloseBusy(false); }
  };

  if (!values) {
    return <AppShell><div className="text-slate-500">Loading settings…</div></AppShell>;
  }

  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const jotformWebhookUrl = `${backendBase}/api/webhooks/jotform`;

  return (
    <AppShell>
      <div className="cq-in max-w-4xl">
        <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Admin</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1">Automation Settings</h1>
        <p className="text-slate-600 mt-2">
          Configure the Jotform → ClientQuack → Close CRM automation. Every Jotform submission auto-creates
          a client portal, syncs to Close, and sends the welcome email + SMS.
        </p>

        {/* Webhook URL banner */}
        <Card className="p-5 rounded-2xl border-amber-200 bg-amber-50/60 mt-6" data-testid="webhook-url-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0">
              <Webhook size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-widest font-bold text-amber-700">Jotform webhook URL</p>
              <p className="text-sm text-slate-700 mt-1">
                Paste this into <span className="font-mono text-xs bg-white px-1.5 py-0.5 rounded">Jotform → Settings → Integrations → Webhooks</span>.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <code className="flex-1 min-w-0 truncate text-xs sm:text-sm bg-white border border-amber-200 rounded-lg px-3 py-2 font-mono text-slate-800" data-testid="jotform-webhook-url">
                  {jotformWebhookUrl}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyUrl(jotformWebhookUrl, "jotform")}
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  data-testid="copy-webhook-url"
                >
                  {copied === "jotform" ? <CheckCircle2 size={14} className="mr-1.5" /> : <Copy size={14} className="mr-1.5" />}
                  {copied === "jotform" ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>
          </div>
        </Card>

        <Tabs defaultValue="jotform" className="mt-6" data-testid="settings-tabs">
          <TabsList className="grid grid-cols-3 sm:grid-cols-6 sm:w-fit bg-white border border-slate-200 rounded-2xl p-1.5 h-auto">
            <TabsTrigger value="jotform" data-testid="tab-jotform" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-xl px-4 py-2 text-sm font-bold">Jotform</TabsTrigger>
            <TabsTrigger value="close" data-testid="tab-close" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-xl px-4 py-2 text-sm font-bold">Close CRM</TabsTrigger>
            <TabsTrigger value="sms" data-testid="tab-sms" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-xl px-4 py-2 text-sm font-bold">SMS Alerts</TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-xl px-4 py-2 text-sm font-bold">Templates</TabsTrigger>
            <TabsTrigger value="general" data-testid="tab-general" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-xl px-4 py-2 text-sm font-bold">General</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-xl px-4 py-2 text-sm font-bold">Security</TabsTrigger>
          </TabsList>

          {/* -------- Jotform -------- */}
          <TabsContent value="jotform" className="mt-6 space-y-4">
            <SectionCard icon={KeyRound} title="Jotform credentials" subtitle="Used to validate incoming webhook submissions.">
              <SecretField
                label="Webhook secret"
                hint="Set this same value in Jotform's webhook integration. Supports either a shared secret header or HMAC signature."
                value={values.jotform_webhook_secret}
                isSet={values.jotform_webhook_secret_is_set}
                onChange={(v) => patch("jotform_webhook_secret", v)}
                testid="jotform-webhook-secret"
              />
              <TextField
                label="Jotform API key (optional)"
                hint="Only needed if we later fetch submissions on-demand via the Jotform API."
                value={values.jotform_api_key}
                onChange={(v) => patch("jotform_api_key", v)}
                testid="jotform-api-key"
              />
              <SaveRow busy={busy} onClick={() => save(["jotform_webhook_secret", "jotform_api_key"])} testid="save-jotform" />
            </SectionCard>
          </TabsContent>

          {/* -------- Close CRM -------- */}
          <TabsContent value="close" className="mt-6 space-y-4">
            <SectionCard icon={KeyRound} title="Close CRM API" subtitle="Enables lead upsert + sequence enrollment.">
              <SecretField
                label="Close API key"
                hint="Settings → API Keys in Close. We use HTTP Basic Auth with this as the username."
                value={values.close_api_key}
                isSet={values.close_api_key_is_set}
                onChange={(v) => patch("close_api_key", v)}
                testid="close-api-key"
              />
              <SecretField
                label="Close webhook secret"
                hint="Optional — used only for the /api/webhooks/close inbound endpoint."
                value={values.close_webhook_secret}
                isSet={values.close_webhook_secret_is_set}
                onChange={(v) => patch("close_webhook_secret", v)}
                testid="close-webhook-secret"
              />
              <SaveRow busy={busy} onClick={() => save(["close_api_key", "close_webhook_secret"])} testid="save-close-keys" />
            </SectionCard>

            <SectionCard icon={Mail} title="Close sequences" subtitle="Which pre-built sequences to enroll every new client in.">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={fetchCloseResources}
                  disabled={closeBusy || !values.close_api_key_is_set}
                  className="border-slate-300"
                  data-testid="fetch-close-resources"
                >
                  <RefreshCw size={14} className={`mr-1.5 ${closeBusy ? "animate-spin" : ""}`} />
                  {closeBusy ? "Fetching…" : "Fetch from Close"}
                </Button>
                {closeRes?.me && (
                  <span className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1">
                    <CheckCircle2 size={13} /> Connected as {closeRes.me.name || closeRes.me.email} · {closeRes.me.org}
                  </span>
                )}
                {!values.close_api_key_is_set && (
                  <span className="text-xs text-slate-500">Save a Close API key first ↑</span>
                )}
              </div>

              {closeRes?.sequences?.some((s) => s.status === "paused") && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-xs text-amber-800">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>Some sequences are <b>paused</b> in Close — enrollment will succeed but Close won't send anything until you flip them to Active in Close → Sequences.</span>
                </div>
              )}

              <div className="rounded-xl bg-sky-50 border border-sky-200 p-3 flex items-start gap-2 text-xs text-sky-900" data-testid="close-plan-notice">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  <b>Close plan note:</b> Sequence enrollment requires the Close <b>Pro plan or higher</b>. On Basic, the lead is still
                  created and every intake field is stored in Close — but the welcome email/SMS will be sent by our built-in
                  <b> Resend + Twilio</b> path instead (configure them below in Templates + backend/.env).
                </span>
              </div>

              <SequencePicker
                label="Welcome email sequence"
                placeholder="seq_..."
                items={(closeRes?.sequences) || []}
                value={values.close_email_sequence_id}
                onChange={(v) => patch("close_email_sequence_id", v)}
                testid="close-email-sequence-id"
              />
              <SequencePicker
                label="Email sender account"
                placeholder="emailacct_..."
                items={(closeRes?.email_accounts || []).map((a) => ({ id: a.id, name: a.email || a.name, status: "" }))}
                value={values.close_sender_account_id}
                onChange={(v) => patch("close_sender_account_id", v)}
                testid="close-sender-account-id"
                hint="The Close-connected email address that welcomes will be sent from."
              />
              <SequencePicker
                label="Welcome SMS sequence"
                placeholder="seq_..."
                items={(closeRes?.sequences) || []}
                value={values.close_sms_sequence_id}
                onChange={(v) => patch("close_sms_sequence_id", v)}
                testid="close-sms-sequence-id"
                hint="Optional — enrollment succeeds only when Close SMS is enabled on the org."
              />

              <SaveRow busy={busy} onClick={() => save(["close_email_sequence_id", "close_sender_account_id", "close_sms_sequence_id", "close_sms_sender_id"])} testid="save-close-sequences" />
            </SectionCard>
          </TabsContent>

          {/* -------- SMS Alerts -------- */}
          <TabsContent value="sms" className="mt-6">
            <SmsAutomationsTab />
          </TabsContent>

          {/* -------- Templates -------- */}
          <TabsContent value="templates" className="mt-6 space-y-4">
            <SectionCard icon={Mail} title="Welcome email" subtitle="Sent via Resend as a fallback whenever a client account is created (Close sequences run in parallel).">
              <TextField label="Subject line" value={values.welcome_email_subject} onChange={(v) => patch("welcome_email_subject", v)} testid="welcome-email-subject" />
              <TextArea
                label="Body"
                rows={14}
                value={values.welcome_email_template}
                onChange={(v) => patch("welcome_email_template", v)}
                hint="Placeholders: {{first_name}}, {{email}}, {{temporary_password}}, {{portal_login_url}}"
                testid="welcome-email-template"
              />
              <SaveRow busy={busy} onClick={() => save(["welcome_email_subject", "welcome_email_template"])} testid="save-email-tpl" />
            </SectionCard>

            <SectionCard icon={MessageSquare} title="Welcome SMS" subtitle="Sent via Twilio when the client has a phone number on file.">
              <TextArea
                label="SMS body"
                rows={8}
                value={values.welcome_sms_template}
                onChange={(v) => patch("welcome_sms_template", v)}
                hint="Keep under ~320 characters. Placeholders: {{first_name}}, {{email}}, {{temporary_password}}, {{portal_login_url}}"
                testid="welcome-sms-template"
              />
              <SaveRow busy={busy} onClick={() => save(["welcome_sms_template"])} testid="save-sms-tpl" />
            </SectionCard>
          </TabsContent>

          {/* -------- General -------- */}
          <TabsContent value="general" className="mt-6 space-y-4">
            <SectionCard icon={ExternalLink} title="Portal & support" subtitle="Public-facing links used inside emails and SMS.">
              <TextField label="Portal login URL" value={values.portal_login_url} onChange={(v) => patch("portal_login_url", v)} placeholder="https://clientquack.com/login" testid="portal-login-url" />
              <TextField label="Support email" value={values.support_email} onChange={(v) => patch("support_email", v)} placeholder="support@creditquack.com" testid="support-email" />
              <TextField label="Support phone" value={values.support_phone} onChange={(v) => patch("support_phone", v)} testid="support-phone" />
              <SaveRow busy={busy} onClick={() => save(["portal_login_url", "support_email", "support_phone"])} testid="save-general" />
            </SectionCard>
          </TabsContent>

          {/* -------- Security -------- */}
          <TabsContent value="security" className="mt-6 space-y-4">
            <SectionCard icon={Lock} title="Secret access codes" subtitle="Hidden one-shot login codes for the Konami / Ctrl+Shift+C Easter egg. Rotate these to invalidate the old codes instantly.">
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 leading-relaxed">
                <p className="font-bold text-slate-800 mb-1">How it works</p>
                Enter either code at the hidden modal (opened via <code className="bg-white border border-slate-200 rounded px-1 py-0.5 font-mono">Ctrl/Cmd + Shift + C</code>, the Konami code, or the tiny duck footer button). The admin code logs into the admin dashboard; the client code logs into the client portal. Codes are stored server-side only and never exposed via any GET response — even to you.
              </div>
              <SecretField
                label="Admin access code"
                hint="Grants entry as the primary super_admin. Case-sensitive. Keep it long, unique, and off-brand."
                value={values.secret_admin_code}
                isSet={values.secret_admin_code_is_set}
                onChange={(v) => patch("secret_admin_code", v)}
                testid="secret-admin-code"
              />
              <SecretField
                label="Client access code"
                hint="Grants entry as the demo client account (for quick tours). Case-sensitive."
                value={values.secret_client_code}
                isSet={values.secret_client_code_is_set}
                onChange={(v) => patch("secret_client_code", v)}
                testid="secret-client-code"
              />
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-xs text-amber-900">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  <b>Heads up:</b> the moment you save a new value, the previous code stops working everywhere. If a field is left blank, it keeps the current stored value.
                </span>
              </div>
              <SaveRow busy={busy} onClick={() => save(["secret_admin_code", "secret_client_code"])} testid="save-security" />
            </SectionCard>
          </TabsContent>
        </Tabs>

        <Card className="p-5 rounded-2xl border-slate-100 mt-8 bg-slate-50" data-testid="test-webhook-card">
          <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Bulk actions</p>
          <p className="font-display font-bold text-slate-900 mt-1">Backfill Close leads → ClientQuack</p>
          <p className="text-sm text-slate-600 mt-1">
            Create a portal account for every existing lead in your Close CRM. Each account uses the deterministic password
            <code className="mx-1 px-1.5 py-0.5 bg-white border border-slate-200 rounded text-xs">LastName + last4ofPhone</code>
            (e.g. <code className="mx-0.5 px-1.5 py-0.5 bg-white border border-slate-200 rounded text-xs">Smith8899</code>). Existing accounts are skipped safely.
          </p>
          <Button
            onClick={runBackfill}
            disabled={closeBusy || !values.close_api_key_is_set}
            className="mt-3 bg-slate-900 hover:bg-slate-800"
            data-testid="run-close-backfill"
          >
            {closeBusy ? "Running…" : "Run Close backfill"}
          </Button>
        </Card>
      </div>
    </AppShell>
  );
}

/* --- helpers --- */

function SectionCard({ icon: Icon, title, subtitle, children }) {
  return (
    <Card className="p-6 rounded-2xl border-slate-100">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
          <Icon size={18} />
        </div>
        <div>
          <h3 className="font-display font-bold text-lg text-slate-900">{title}</h3>
          {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </Card>
  );
}

function TextField({ label, value, onChange, placeholder, hint, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</Label>
      <Input
        className="mt-1"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
      />
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function SecretField({ label, value, isSet, onChange, hint, testid }) {
  const [reveal, setReveal] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</Label>
        {isSet && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Configured</Badge>}
      </div>
      <div className="flex gap-2 mt-1">
        <Input
          type={reveal ? "text" : "password"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isSet ? "Leave blank to keep existing value" : "Not set"}
          data-testid={testid}
        />
        <Button type="button" variant="outline" size="sm" onClick={() => setReveal((s) => !s)}>
          {reveal ? "Hide" : "Show"}
        </Button>
      </div>
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function TextArea({ label, value, onChange, hint, rows = 6, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</Label>
      <Textarea
        className="mt-1 font-mono text-sm"
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function SaveRow({ busy, onClick, testid }) {
  return (
    <div className="pt-2">
      <Button
        onClick={onClick}
        disabled={busy}
        className="bg-amber-500 hover:bg-amber-600"
        data-testid={testid}
      >
        <Save size={14} className="mr-1.5" /> {busy ? "Saving…" : "Save changes"}
      </Button>
    </div>
  );
}

function SequencePicker({ label, placeholder, items, value, onChange, hint, testid }) {
  const hasItems = Array.isArray(items) && items.length > 0;
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</Label>
      {hasItems ? (
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
        >
          <option value="">— Select —</option>
          {items.map((it) => (
            <option key={it.id} value={it.id}>
              {it.name || it.id}{it.status ? ` · ${it.status}` : ""}
            </option>
          ))}
        </select>
      ) : (
        <Input
          className="mt-1"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
        />
      )}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}
